<script setup lang="ts">

import type { IMyGarageViewModel } from "../..";
import { useAppConfiguration } from "@domain/AppConfiguration";
import { useUserSettings } from "@services/UserSettings";
import { comma } from "@library/Utilities"; 
import { isDemo } from "@modules/Utilities/Environments";

const model = defineModel<IMyGarageViewModel>( { required: true } );
const app = useAppConfiguration();
const userSettings = useUserSettings();
const isDemoSite = isDemo();

</script>

<template>

    <div class="w-full flex flex-col">

        <h1 class="flex gap-3 items-start justify-between
                   px-4 3xl:px-6 w-full 3xl:text-3xl mb-3 lg:mb-4">

            <div class="flex-1 flex flex-col gap-1 3xl:gap-3">

                <!-- VEHICLE NICKNAME / SWITCH -->
                <div class="flex items-center justify-start gap-2 3xl:gap-4">
                    
                    <div class="
                        font-bold text-2xl 2xl:text-3xl -mt-4 px-4 2xl:px-6 -ml-4 2xl:-ml-6
                        h-12 mr-auto pt-3 2xl:pt-4 rounded-t-md 2xl:rounded-t-lg bg-gray-850
                        max-w-full truncate
                        ">

                        {{ model.vehicle.nickname ?? "Vehicle Details" }}

                        <div class="h-4 w-4 bg-gray-850 absolute -right-4 bottom-0">
                            <div class="h-4 w-4 bg-gray-900 rounded-bl-[30%]" />
                        </div>
                    </div>

                </div>

                <!-- DETAILS ROW ONE -->
                <div class="flex items-center gap-3 2xl:gap-8 -mr-12
                            text-xs md:text-sm 2xl:text-lg 2xl:mb-1">

                    <!-- REGO -->
                    <div class="flex items-center gap-1 uppercase pl-0.5">
                        <Icon name="rego" class="text-xl 3xl:text-3xl opacity-60 mr-0.5 3xl:mr-2" />
                        <span class="min-w-[60px]">{{ model.vehicle.rego }}</span>
                    </div>

                    <!-- ODOMETER -->
                    <div class="flex items-center gap-1 uppercase pl-0.5">
                        <Icon name="odometer" class="text-xl 3xl:text-2xl opacity-60 mr-0.5 3xl:mr-2 3xl:ml-1" />
                        <span>{{ comma( model.vehicle.odometer ) }}km</span>
                    </div>

                    <!-- SHARED INDICATOR -->
                    <div v-if="model.vehicle.isShared"
                         class="flex items-center gap-1 uppercase whitespace-nowrap pl-0.5">
                        <Icon name="connect" class="text-xl 2xl:text-3xl opacity-60 mr-0.5 3xl:mr-2" />
                        <span>Shared</span>
                    </div>

                </div>

                <!-- DETAILS ROW TWO -->
                <!-- <div class="flex items-center gap-3 3xl:gap-8 -mr-12
                            text-xs md:text-sm 3xl:text-lg"
                     :class="{ '!block': model.carePlan.healthMonitor.hasIssueDetected || model.carePlan.healthMonitor.isInstalled == false }">

                    <div v-if="model.carePlan.healthMonitor.isEnabled && model.carePlan.healthMonitor.isInstalled"
                         class="flex items-center gap-1 uppercase">
                        <Icon name="special:health-monitor" class="text-2xl 3xl:text-4xl 3xl:mr-1.5 opacity-80 top-px" />
                        <span v-if="model.carePlan.healthMonitor.hasIssueDetected" class="text-rose-500">
                            Issue Detected
                        </span>
                        <span v-else-if="model.carePlan.healthMonitor.isInstalled && model.carePlan.healthMonitor.isPaired" class="min-w-[60px]">
                            Active
                        </span>
                        <span v-else-if="model.carePlan.healthMonitor.isInstalled" class="min-w-[60px]">
                            Installed
                        </span>
                        <span v-else
                              class="opacity-40">
                            Not Installed
                        </span>
                    </div>

                    <div v-if="model.carePlan.theftRecovery.isEnabled"
                         class="flex items-center gap-1 uppercase">
                        <Icon name="special:theft-recovery" class="text-2xl 3xl:text-4xl 3xl:mr-1 opacity-80" />
                        <span v-if="model.carePlan.theftRecovery.isInstalled" class="min-w-[60px]">
                            Theft Recovery Active
                        </span>
                        <span v-else
                              class="opacity-40">
                            Theft Recovery Inactive
                        </span>
                    </div>

                </div> -->

            </div>

            <!-- SWITCH VEHICLE / EXPAND PROFILE BUTTON -->
            <div class="flex items-center gap-2 -mr-2 top-2 -ml-6">

                <JupiterButton 
                    v-if="!isDemoSite"
                    variant="text"
                    type="page"
                    to="settings"
                    class="!min-w-0 px-0 mr-2"
                    :ripple="false">
                    <span class="text-xs 3xl:text-sm uppercase text-primary-500 dark:text-secondary-400 tracking-normal top-px">
                        Switch
                    </span>
                </JupiterButton>


                <div class="rounded-full transition-all duration-300"
                    :class="{
                        'rotate-180': userSettings.expandProfileActions
                    }">
                            
                    <IconButton :name="userSettings.expandProfileActions ? 'cross' : 'ellipsis-vert'"
                                @click="userSettings.expandProfileActions = !userSettings.expandProfileActions" />
                </div>
            </div>

        </h1>

    </div>

</template>
